import { useEffect, useRef } from "react";
import { ackMailNotifications, fetchPendingMailNotifications } from "@/lib/api/notifications";
import {
  MAIL_BACKGROUND_SYNC_MS,
  MAIL_LIVE_SYNC_INITIAL_DELAY_MS,
  MAIL_LIVE_SYNC_MS,
} from "@/lib/constants/inbox";
import {
  idleDelay,
  isStandardBootstrapFolder,
  sortFoldersForBootstrap,
  syncFolderFromServerCache,
  syncFolderHeaders,
} from "@/lib/mail/folderWarmup";
import { notifyMailLiveSyncDone } from "@/lib/mail/mailLiveSyncEvents";
import {
  MAIL_NEW_MAIL_EVENT,
  type MailNewMailDetail,
} from "@/lib/mail/mailNewMailEvents";
import { isDraftSaveInFlight } from "@/lib/compose/draftSaveLock";
import { isOutboxSendInFlight } from "@/lib/outbox/outboxSendLock";
import { isImapClientBusy } from "@/lib/mail/imapClientLock";
import { unlockMailNotificationSound } from "@/lib/mail/mailNotificationSound";
import type { MailFolder } from "@/lib/types/mail";

const STANDARD_FOLDER_GAP_MS = 150;
/** Retour sur l’onglet : relire le dossier ouvert, sans attendre le cycle de 5 min. */
const VISIBILITY_COOLDOWN_MS = 2_000;
const RECEPTION_IMAP_WAIT_MAX_MS = 12_000;
const RECEPTION_IMAP_WAIT_STEP_MS = 400;

/** Réception planifiée : ne pas bloquer sur mutations locales (move/snooze en cours). */
async function waitForReceptionImapSlot(cancelled: () => boolean): Promise<boolean> {
  let waited = 0;
  while (
    !cancelled() &&
    (isDraftSaveInFlight() || isOutboxSendInFlight() || isImapClientBusy())
  ) {
    if (waited >= RECEPTION_IMAP_WAIT_MAX_MS) return false;
    await idleDelay(RECEPTION_IMAP_WAIT_STEP_MS);
    waited += RECEPTION_IMAP_WAIT_STEP_MS;
  }
  return !cancelled();
}

type Options = {
  accountId?: number;
  folder?: string;
  folders?: MailFolder[];
  enabled?: boolean;
  /** Pas de fallback IMAP sur les dossiers secondaires tant qu’un message est ouvert. */
  pauseImapWhileReading?: boolean;
  /** Bloque sync IMAP headers secondaires (ex. brouillon en cours). */
  pauseImapFully?: boolean;
};

/**
 * Dossier affiché tout de suite, puis toutes les 5 min le reste :
 * 1. Réception (ou dossier ouvert) — en-têtes incrémentaux
 * 2. Au bout de 20 s : notifs, autres dossiers
 */
export function useMailLiveSync({
  accountId,
  folder = "INBOX",
  folders = [],
  enabled = true,
  pauseImapWhileReading = false,
  pauseImapFully = false,
}: Options): void {
  const foldersRef = useRef(folders);
  foldersRef.current = folders;

  const folderRef = useRef(folder);
  folderRef.current = folder;

  const runningRef = useRef(false);
  const lastOtherFoldersSyncRef = useRef(0);
  const lastVisiblePollRef = useRef(0);
  const pauseImapRef = useRef(pauseImapWhileReading);
  pauseImapRef.current = pauseImapWhileReading;
  const pauseImapFullyRef = useRef(pauseImapFully);
  pauseImapFullyRef.current = pauseImapFully;

  useEffect(() => {
    const unlock = () => unlockMailNotificationSound();
    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  useEffect(() => {
    if (!enabled || accountId == null) return;

    const onNewMailSync = (event: Event) => {
      const detail = (event as CustomEvent<MailNewMailDetail>).detail;
      if (detail?.accountId !== accountId || detail.newCount < 1) return;
      if (detail.folder === folderRef.current) return;
      void syncFolderHeaders(accountId, detail.folder);
    };

    window.addEventListener(MAIL_NEW_MAIL_EVENT, onNewMailSync);
    return () => window.removeEventListener(MAIL_NEW_MAIL_EVENT, onNewMailSync);
  }, [accountId, enabled]);

  useEffect(() => {
    if (!enabled || accountId == null) return;

    let cancelled = false;

    const standardFolders = () =>
      sortFoldersForBootstrap(foldersRef.current).filter((f) =>
        isStandardBootstrapFolder(f.name)
      );

    const syncStandardFolders = async (includeActive = true) => {
      const activeFolder = folderRef.current;
      const targets = standardFolders().filter(
        (f) => includeActive || f.name !== activeFolder
      );

      for (const f of targets) {
        if (cancelled) break;
        const result = await syncFolderFromServerCache(accountId, f.name);
        if (result === "miss" || result === "fail") {
          if (await waitForReceptionImapSlot(() => cancelled)) {
            await syncFolderHeaders(accountId, f.name);
          }
        }
        await idleDelay(STANDARD_FOLDER_GAP_MS);
      }
    };

    const syncOtherFolders = async () => {
      const now = Date.now();
      if (now - lastOtherFoldersSyncRef.current < MAIL_BACKGROUND_SYNC_MS) return;
      lastOtherFoldersSyncRef.current = now;

      const targets = sortFoldersForBootstrap(foldersRef.current).filter(
        (f) => !isStandardBootstrapFolder(f.name)
      );

      for (const f of targets) {
        if (cancelled) break;
        await syncFolderFromServerCache(accountId, f.name);
        await idleDelay(STANDARD_FOLDER_GAP_MS);
      }
    };

    const poll = async () => {
      if (cancelled || document.visibilityState !== "visible" || runningRef.current) return;

      runningRef.current = true;
      const activeFolder = folderRef.current;

      try {
        const notifRes = await fetchPendingMailNotifications(accountId);
        if (cancelled) return;

        const events = notifRes.ok ? (notifRes.data.events ?? []) : [];
        if (events.length > 0) {
          // Son uniquement via notifyNewMailArrived (après sync réelle).
          const eventFolders = [...new Set(events.map((ev) => ev.folder).filter(Boolean))];
          for (const eventFolder of eventFolders) {
            if (cancelled) break;
            await syncFolderFromServerCache(accountId, eventFolder);
            if (
              !pauseImapFullyRef.current &&
              (await waitForReceptionImapSlot(() => cancelled))
            ) {
              await syncFolderHeaders(accountId, eventFolder);
            }
          }
          const ids = events.map((ev) => ev.id).filter((id) => id > 0);
          if (ids.length > 0) {
            await ackMailNotifications(ids);
          }
        }

        if (!pauseImapFullyRef.current && (await waitForReceptionImapSlot(() => cancelled))) {
          await syncFolderHeaders(accountId, activeFolder);
        }
        if (cancelled) return;

        await syncFolderFromServerCache(accountId, activeFolder);

        if (pauseImapRef.current || pauseImapFullyRef.current) {
          return;
        }

        if (await waitForReceptionImapSlot(() => cancelled)) {
          await syncStandardFolders(false);
          await syncOtherFolders();
        }
      } finally {
        runningRef.current = false;
        if (!cancelled) {
          notifyMailLiveSyncDone(accountId, activeFolder);
        }
      }
    };

    const syncActiveFolder = async () => {
      if (cancelled || document.visibilityState !== "visible" || runningRef.current) return;

      runningRef.current = true;
      const activeFolder = folderRef.current;

      try {
        if (!pauseImapFullyRef.current && (await waitForReceptionImapSlot(() => cancelled))) {
          await syncFolderHeaders(accountId, activeFolder);
        }
        if (cancelled) return;
        await syncFolderFromServerCache(accountId, activeFolder);
      } finally {
        runningRef.current = false;
        if (!cancelled) {
          notifyMailLiveSyncDone(accountId, activeFolder);
        }
      }
    };

    void syncActiveFolder();

    const initialTimer = window.setTimeout(() => {
      void poll();
    }, MAIL_LIVE_SYNC_INITIAL_DELAY_MS);

    const intervalId = window.setInterval(() => {
      void poll();
    }, MAIL_LIVE_SYNC_MS);

    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastVisiblePollRef.current < VISIBILITY_COOLDOWN_MS) return;
      lastVisiblePollRef.current = now;
      void syncActiveFolder();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      window.clearTimeout(initialTimer);
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [accountId, enabled]);
}
